import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PageTransition } from "@/components/common/Motion";
import { useEffect, useState } from "react";
import {
  Bell, Calendar, CheckSquare, Home, Image as ImageIcon, LayoutGrid, LineChart,
  LogOut, Send, Settings as SettingsIcon, Share2, ShieldCheck, ChevronsUpDown, Check, Zap,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/common/Logo";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/app", label: "Dashboard", icon: Home, end: true, testid: "nav-dashboard" },
  { to: "/app/composer", label: "Composer", icon: Send, testid: "nav-composer" },
  { to: "/app/campaigns", label: "Campaigns", icon: Zap, testid: "nav-campaigns" },
  { to: "/app/approvals", label: "Approvals", icon: CheckSquare, testid: "nav-approvals" },
  { to: "/app/calendar", label: "Calendar", icon: Calendar, testid: "nav-calendar" },
  { to: "/app/media", label: "Media library", icon: ImageIcon, testid: "nav-media" },
  { to: "/app/social", label: "Social accounts", icon: Share2, testid: "nav-social" },
  { to: "/app/analytics", label: "Analytics", icon: LineChart, testid: "nav-analytics" },
  { to: "/app/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppShell() {
  const location = useLocation();
  const { user, tenants, currentTenant, switchTenant, logout, isSuperAdmin, role, plan } = useAuth();
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setUnread(data.unread || 0);
      } catch (_) {}
    }
    load();
    const int = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(int); };
  }, [currentTenant?.id]);

  if (!currentTenant && !isSuperAdmin) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-[#43535E]">You are not a member of any tenant yet.</p>
        <button onClick={logout} className="hs-btn-primary">Sign out</button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#F6F8FA]">
      <aside className="hidden w-64 shrink-0 border-r border-[#E4E9EF] bg-white md:flex md:flex-col" data-testid="app-sidebar">
        <div className="border-b border-[#E4E9EF] px-5 py-4"><Logo /></div>
        <nav className="flex-1 px-3 py-4">
          <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Workspace</div>
          <ul className="space-y-1">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "text-[#1F5FD8] font-semibold"
                        : "text-[#43535E] hover:bg-[#F6F8FA] hover:text-[#0B2B33]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* One shared highlight that slides between items, rather
                          than a background appearing and disappearing on two
                          separate links. layoutId is what ties them together. */}
                      {isActive ? (
                        <motion.span
                          layoutId="nav-active"
                          aria-hidden="true"
                          className="absolute inset-0 -z-10 rounded-lg bg-[#EEF3FB]"
                          transition={{ type: "spring", stiffness: 420, damping: 36 }}
                        />
                      ) : null}
                      <n.icon className="h-4 w-4" />
                      <span className="flex-1">{n.label}</span>
                      {n.label === "Notifications" && unread > 0 ? (
                        <motion.span
                          key={unread}
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 500, damping: 20 }}
                          className="hs-chip bg-[#E3252A]/10 text-[#E3252A]"
                        >
                          {unread}
                        </motion.span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
          {isSuperAdmin ? (
            <>
              <div className="mt-6 mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Platform</div>
              <ul className="space-y-1">
                <li>
                  <NavLink
                    to="/admin"
                    data-testid="nav-super-admin"
                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-[#43535E] hover:bg-[#F6F8FA] hover:text-[#0B2B33]"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Super Admin Console
                  </NavLink>
                </li>
              </ul>
            </>
          ) : null}
        </nav>
        <div className="border-t border-[#E4E9EF] p-3">
          <div className="rounded-lg bg-[#F6F8FA] p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Plan</div>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-sm font-semibold text-[#0B2B33]">{plan?.name || "—"}</span>
              <button
                data-testid="upgrade-plan-button"
                onClick={() => nav("/app/settings")}
                className="text-xs font-semibold text-[#1F5FD8] hover:underline"
              >
                Manage
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[#E4E9EF] bg-white px-6" data-testid="app-topbar">
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="tenant-switcher-dropdown"
                  className="flex items-center gap-2 rounded-lg border border-[#E4E9EF] px-3 py-1.5 text-sm text-[#0B2B33] hover:bg-[#F6F8FA]"
                >
                  <LayoutGrid className="h-4 w-4 text-[#43535E]" />
                  <span className="font-semibold">{currentTenant?.name || "Platform"}</span>
                  <ChevronsUpDown className="h-4 w-4 text-[#8A97A0]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {tenants.map((t) => (
                  <DropdownMenuItem
                    key={t.id}
                    data-testid={`tenant-option-${t.slug}`}
                    onClick={() => switchTenant(t.id)}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <div className="text-sm font-semibold text-[#0B2B33]">{t.name}</div>
                      <div className="text-xs text-[#8A97A0]">{t.role} · {t.plan_code}</div>
                    </div>
                    {t.id === currentTenant?.id ? <Check className="h-4 w-4 text-[#16A46B]" /> : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="hidden text-xs text-[#8A97A0] md:inline">Role: <span className="font-semibold text-[#43535E]">{role}</span></span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/app/notifications")}
              data-testid="topbar-notifications"
              className="relative rounded-full p-2 text-[#43535E] hover:bg-[#F6F8FA]"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
              {unread > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 h-4 min-w-4 rounded-full bg-[#E3252A] px-1 text-[10px] font-bold leading-4 text-white">{unread}</span>
              ) : null}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-full border border-[#E4E9EF] px-3 py-1.5 hover:bg-[#F6F8FA]">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1F5FD8] text-xs font-bold text-white">
                    {(user?.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="hidden text-sm font-semibold text-[#0B2B33] md:inline">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold text-[#0B2B33]">{user?.name}</div>
                  <div className="text-xs text-[#8A97A0]">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/app/settings")} data-testid="user-menu-settings">Settings</DropdownMenuItem>
                {isSuperAdmin ? (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="user-menu-admin">Super Admin</DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="user-menu-logout" className="text-[#E3252A]">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 md:px-8">
          {/* Keyed on the path so each screen gets one short entrance. The
              shell itself does not move, so navigation reads as the content
              changing rather than the whole app redrawing. */}
          <PageTransition routeKey={location.pathname}>
            <Outlet />
          </PageTransition>
        </main>
      </div>
    </div>
  );
}
